import { useMemo, useState } from 'react';
import type { DataTableSort } from '@bcecloud/portal-ui';

/**
 * Client-side column sort for a small table whose rows are already in hand
 * (breakdown cards, top-N tables). Clicking the active column flips its
 * direction; clicking a new column starts descending — these tables hold
 * counts and dollars, and "biggest first" is the question being asked.
 *
 * @param rows - The rows to sort.
 * @param accessors - Sort key → the value it orders by.
 * @param initial - The sort to open with.
 * @returns Sorted rows plus DataTable's sort/onSort pair.
 */
export function useLocalSort<Row>(
  rows: readonly Row[],
  accessors: Readonly<Record<string, (row: Row) => number | string | null>>,
  initial: DataTableSort,
): { sorted: Row[]; sort: DataTableSort; onSort: (key: string) => void } {
  const [sort, setSort] = useState<DataTableSort>(initial);
  const sorted = useMemo(() => {
    const accessor = accessors[sort.key];
    if (!accessor) return [...rows];
    const direction = sort.direction === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const left = accessor(a);
      const right = accessor(b);
      if (left === null || right === null) return left === right ? 0 : left === null ? 1 : -1;
      if (typeof left === 'string' || typeof right === 'string') {
        return direction * String(left).localeCompare(String(right));
      }
      return direction * (left - right);
    });
  }, [rows, accessors, sort]);

  function onSort(key: string): void {
    setSort((previous) =>
      previous.key === key
        ? { key, direction: previous.direction === 'desc' ? 'asc' : 'desc' }
        : { key, direction: 'desc' },
    );
  }

  return { sorted, sort, onSort };
}
